import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage, LANGUAGES } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { 
    ArrowLeft, User, Camera, Stethoscope, Users, Save, Loader2, 
    CheckCircle2, Settings, Shield, Trash2, AlertTriangle, Mail, Phone,
    Globe, Check
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { language, setLanguage, t, currentLanguage } = useLanguage();
    const fileInputRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [profilePicture, setProfilePicture] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
    const [deactivateConfirmText, setDeactivateConfirmText] = useState('');
    const [deactivating, setDeactivating] = useState(false);
    
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        address: '',
        dateOfBirth: '',
        gpName: '',
        gpPracticeName: '',
        gpAddress: '',
        gpPhone: '',
        gpEmail: '',
        nokName: '',
        nokRelationship: '',
        nokPhone: '',
        nokEmail: '',
        nokAddress: ''
    });

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await axios.get(`${API}/auth/me`);
                const userData = response.data;
                setFormData({
                    name: userData.name || '',
                    phone: userData.phone || '',
                    address: userData.address || '',
                    dateOfBirth: userData.date_of_birth || '',
                    gpName: userData.gp_details?.name || '',
                    gpPracticeName: userData.gp_details?.practice_name || '',
                    gpAddress: userData.gp_details?.address || '',
                    gpPhone: userData.gp_details?.phone || '',
                    gpEmail: userData.gp_details?.email || '',
                    nokName: userData.next_of_kin?.name || '',
                    nokRelationship: userData.next_of_kin?.relationship || '',
                    nokPhone: userData.next_of_kin?.phone || '',
                    nokEmail: userData.next_of_kin?.email || '',
                    nokAddress: userData.next_of_kin?.address || ''
                });
                if (userData.profile_picture) {
                    setPreviewUrl(userData.profile_picture);
                }
            } catch (err) {
                console.error('Failed to fetch profile:', err);
            }
        };
        fetchProfile();
    }, []);

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image must be less than 5MB');
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfilePicture(reader.result);
                setPreviewUrl(reader.result);
                toast.success('Profile picture selected');
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            await axios.put(`${API}/auth/profile`, {
                name: formData.name,
                phone: formData.phone,
                address: formData.address,
                date_of_birth: formData.dateOfBirth,
                profile_picture: profilePicture,
                gp_name: formData.gpName,
                gp_practice_name: formData.gpPracticeName,
                gp_address: formData.gpAddress,
                gp_phone: formData.gpPhone,
                gp_email: formData.gpEmail,
                next_of_kin_name: formData.nokName,
                next_of_kin_relationship: formData.nokRelationship,
                next_of_kin_phone: formData.nokPhone,
                next_of_kin_email: formData.nokEmail,
                next_of_kin_address: formData.nokAddress
            });
            toast.success('Settings saved successfully!');
        } catch (err) {
            toast.error('Failed to save settings');
        } finally {
            setLoading(false);
        }
    };

    const handleDeactivateAccount = async () => {
        if (deactivateConfirmText.toLowerCase() !== 'deactivate') {
            toast.error('Please type "deactivate" to confirm');
            return;
        }

        setDeactivating(true);
        try {
            await axios.post(`${API}/auth/deactivate`);
            toast.success('Your account has been deactivated');
            logout();
            navigate('/');
        } catch (err) {
            toast.error('Failed to deactivate account. Please try again.');
        } finally {
            setDeactivating(false);
            setShowDeactivateDialog(false);
        }
    };

    return (
        <div className="min-h-screen bg-background py-8 lg:py-12" data-testid="settings-page">
            <div className="max-w-3xl mx-auto px-4">
                <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
                    <ArrowLeft className="w-4 h-4 mr-2" /> {t('backToDashboard')}
                </Button>
                
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Settings className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                        <h1 className="font-heading text-3xl font-bold">{t('settings')}</h1>
                        <p className="text-muted-foreground">{t('manageProfile')}</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="mt-8">
                    <Tabs defaultValue="profile" className="space-y-6">
                        <TabsList className="grid grid-cols-5 h-auto">
                            <TabsTrigger value="profile" className="flex flex-col sm:flex-row items-center gap-1 py-2">
                                <User className="w-4 h-4" />
                                <span className="text-xs sm:text-sm">{t('profile')}</span>
                            </TabsTrigger>
                            <TabsTrigger value="gp" className="flex flex-col sm:flex-row items-center gap-1 py-2">
                                <Stethoscope className="w-4 h-4" />
                                <span className="text-xs sm:text-sm">{t('gpDetails')}</span>
                            </TabsTrigger>
                            <TabsTrigger value="kin" className="flex flex-col sm:flex-row items-center gap-1 py-2">
                                <Users className="w-4 h-4" />
                                <span className="text-xs sm:text-sm">{t('nextOfKin')}</span>
                            </TabsTrigger>
                            <TabsTrigger value="language" className="flex flex-col sm:flex-row items-center gap-1 py-2">
                                <Globe className="w-4 h-4" />
                                <span className="text-xs sm:text-sm">{t('language')}</span>
                            </TabsTrigger>
                            <TabsTrigger value="account" className="flex flex-col sm:flex-row items-center gap-1 py-2">
                                <Shield className="w-4 h-4" />
                                <span className="text-xs sm:text-sm">{t('account')}</span>
                            </TabsTrigger>
                        </TabsList>

                        {/* Profile Tab */}
                        <TabsContent value="profile">
                            <Card>
                                <CardHeader>
                                    <CardTitle>{t('profileInformation')}</CardTitle>
                                    <CardDescription>{t('updatePersonalDetails')}</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {/* Profile Picture */}
                                    <div className="flex flex-col items-center gap-4 pb-6 border-b">
                                        <div 
                                            className="relative w-32 h-32 rounded-full bg-muted flex items-center justify-center overflow-hidden cursor-pointer group border-4 border-background shadow-lg"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            {previewUrl ? (
                                                <img src={previewUrl} alt="Profile" className="w-full h-full object-cover" />
                                            ) : (
                                                <User className="w-16 h-16 text-muted-foreground" />
                                            )}
                                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Camera className="w-8 h-8 text-white" />
                                            </div>
                                        </div>
                                        <input 
                                            ref={fileInputRef} 
                                            type="file" 
                                            accept="image/*" 
                                            onChange={handleImageUpload} 
                                            className="hidden" 
                                        />
                                        <div className="text-center">
                                            <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                                                <Camera className="w-4 h-4 mr-2" />
                                                {t('changePhoto')}
                                            </Button>
                                            <p className="text-xs text-muted-foreground mt-2">{t('jpgPngGif')}</p>
                                        </div>
                                    </div>
                                    
                                    {/* Personal Details */}
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>{t('fullName')}</Label>
                                            <Input 
                                                value={formData.name} 
                                                onChange={(e) => setFormData({...formData, name: e.target.value})} 
                                                placeholder="John Doe"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>{t('email')}</Label>
                                            <Input value={user?.email || ''} disabled className="bg-muted" />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>{t('phoneNumber')}</Label>
                                            <Input 
                                                value={formData.phone} 
                                                onChange={(e) => setFormData({...formData, phone: e.target.value})} 
                                                placeholder="+1 234 567 8900"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>{t('dateOfBirth')}</Label>
                                            <Input 
                                                type="date"
                                                value={formData.dateOfBirth} 
                                                onChange={(e) => setFormData({...formData, dateOfBirth: e.target.value})} 
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>{t('address')}</Label>
                                        <Textarea 
                                            value={formData.address} 
                                            onChange={(e) => setFormData({...formData, address: e.target.value})} 
                                            placeholder="123 Main Street, City, Country"
                                            rows={2}
                                        />
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* GP Details Tab */}
                        <TabsContent value="gp">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Stethoscope className="w-5 h-5 text-primary" />
                                        {t('gpDoctorDetails')}
                                    </CardTitle>
                                    <CardDescription>
                                        {t('primaryCarePhysician')}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>{t('doctorName')}</Label>
                                            <Input 
                                                value={formData.gpName} 
                                                onChange={(e) => setFormData({...formData, gpName: e.target.value})} 
                                                placeholder="Dr. Sarah Smith"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Practice/Surgery Name</Label>
                                            <Input 
                                                value={formData.gpPracticeName} 
                                                onChange={(e) => setFormData({...formData, gpPracticeName: e.target.value})} 
                                                placeholder="City Medical Centre"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Practice Address</Label>
                                        <Textarea 
                                            value={formData.gpAddress} 
                                            onChange={(e) => setFormData({...formData, gpAddress: e.target.value})} 
                                            placeholder="123 Medical Street, City, Postcode"
                                            rows={2}
                                        />
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="flex items-center gap-2">
                                                <Phone className="w-4 h-4" />
                                                Phone Number
                                            </Label>
                                            <Input 
                                                value={formData.gpPhone} 
                                                onChange={(e) => setFormData({...formData, gpPhone: e.target.value})} 
                                                placeholder="+44 20 1234 5678"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="flex items-center gap-2">
                                                <Mail className="w-4 h-4" />
                                                Email
                                            </Label>
                                            <Input 
                                                type="email"
                                                value={formData.gpEmail} 
                                                onChange={(e) => setFormData({...formData, gpEmail: e.target.value})} 
                                                placeholder="surgery@example.com"
                                            />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Next of Kin Tab */}
                        <TabsContent value="kin">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Users className="w-5 h-5 text-primary" />
                                        Next of Kin / Emergency Contact
                                    </CardTitle>
                                    <CardDescription>
                                        Emergency contact information for medical situations
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Full Name</Label>
                                            <Input 
                                                value={formData.nokName} 
                                                onChange={(e) => setFormData({...formData, nokName: e.target.value})} 
                                                placeholder="Jane Doe"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Relationship</Label>
                                            <Input 
                                                value={formData.nokRelationship} 
                                                onChange={(e) => setFormData({...formData, nokRelationship: e.target.value})} 
                                                placeholder="Spouse, Parent, Sibling, Child..."
                                            />
                                        </div>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="flex items-center gap-2">
                                                <Phone className="w-4 h-4" />
                                                Phone Number
                                            </Label>
                                            <Input 
                                                value={formData.nokPhone} 
                                                onChange={(e) => setFormData({...formData, nokPhone: e.target.value})} 
                                                placeholder="+44 7700 900000"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="flex items-center gap-2">
                                                <Mail className="w-4 h-4" />
                                                Email
                                            </Label>
                                            <Input 
                                                type="email"
                                                value={formData.nokEmail} 
                                                onChange={(e) => setFormData({...formData, nokEmail: e.target.value})} 
                                                placeholder="jane@example.com"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Address</Label>
                                        <Textarea 
                                            value={formData.nokAddress} 
                                            onChange={(e) => setFormData({...formData, nokAddress: e.target.value})} 
                                            placeholder="123 Emergency Lane, City, Postcode"
                                            rows={2}
                                        />
                                    </div>

                                    <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                        <p className="text-sm text-blue-800 dark:text-blue-200">
                                            <strong>Note:</strong> This information will only be used in case of medical emergency and will be shared with healthcare providers when necessary.
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Language Tab */}
                        <TabsContent value="language">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Globe className="w-5 h-5 text-primary" />
                                        {t('languageSettings') || 'Language Settings'}
                                    </CardTitle>
                                    <CardDescription>
                                        {t('selectPreferredLanguage') || 'Select your preferred language for the interface'}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                        {LANGUAGES.map((lang) => (
                                            <button
                                                key={lang.code}
                                                type="button"
                                                onClick={() => {
                                                    setLanguage(lang.code);
                                                    toast.success(`Language changed to ${lang.name}`);
                                                }}
                                                className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all hover:border-primary/50 hover:bg-primary/5 ${
                                                    language === lang.code 
                                                        ? 'border-primary bg-primary/10' 
                                                        : 'border-slate-200 dark:border-slate-700'
                                                }`}
                                                data-testid={`lang-${lang.code}`}
                                            >
                                                {language === lang.code && (
                                                    <div className="absolute top-2 right-2">
                                                        <Check className="w-4 h-4 text-primary" />
                                                    </div>
                                                )}
                                                <span className="text-2xl">{lang.flag}</span>
                                                <div className="text-center">
                                                    <p className="font-medium text-sm">{lang.name}</p>
                                                    <p className="text-xs text-muted-foreground">{lang.nativeName}</p>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                    
                                    <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl">
                                        <p className="text-sm text-blue-700 dark:text-blue-300">
                                            <strong>{t('currentLanguage') || 'Current language'}:</strong> {currentLanguage.flag} {currentLanguage.name} ({currentLanguage.nativeName})
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {t('someMedicalContent') || 'Note: Some medical content may still appear in English for accuracy.'}
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Account Tab */}
                        <TabsContent value="account">
                            <div className="space-y-6">
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Shield className="w-5 h-5 text-primary" />
                                            Account Security
                                        </CardTitle>
                                        <CardDescription>Manage your account security settings</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="flex items-center justify-between p-4 border rounded-lg">
                                            <div>
                                                <p className="font-medium">Password</p>
                                                <p className="text-sm text-muted-foreground">Change your account password</p>
                                            </div>
                                            <Button variant="outline" onClick={() => navigate('/forgot-password')}>
                                                Change Password
                                            </Button>
                                        </div>
                                        <div className="flex items-center justify-between p-4 border rounded-lg">
                                            <div>
                                                <p className="font-medium">Email</p>
                                                <p className="text-sm text-muted-foreground">{user?.email}</p>
                                            </div>
                                            <Button variant="outline" disabled>
                                                Verified <CheckCircle2 className="w-4 h-4 ml-2 text-green-500" />
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Danger Zone */}
                                <Card className="border-red-200 dark:border-red-800">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
                                            <AlertTriangle className="w-5 h-5" />
                                            Danger Zone
                                        </CardTitle>
                                        <CardDescription>Irreversible account actions</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-800">
                                            <div>
                                                <p className="font-medium text-red-800 dark:text-red-200">Deactivate Account</p>
                                                <p className="text-sm text-red-600 dark:text-red-400">
                                                    Permanently delete your account and all associated data. This action cannot be undone.
                                                </p>
                                            </div>
                                            <Button 
                                                variant="destructive" 
                                                onClick={() => setShowDeactivateDialog(true)}
                                                className="flex-shrink-0"
                                            >
                                                <Trash2 className="w-4 h-4 mr-2" />
                                                Deactivate
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </TabsContent>
                    </Tabs>

                    {/* Save Button */}
                    <div className="mt-6 flex justify-end">
                        <Button type="submit" disabled={loading} className="min-w-[140px]">
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4 mr-2" />
                                    Save Changes
                                </>
                            )}
                        </Button>
                    </div>
                </form>

                {/* Deactivate Account Dialog */}
                <Dialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-red-600">
                                <AlertTriangle className="w-5 h-5" />
                                Deactivate Account
                            </DialogTitle>
                            <DialogDescription>
                                This action is permanent and cannot be undone. All your data including:
                                <ul className="list-disc list-inside mt-2 space-y-1">
                                    <li>Profile information</li>
                                    <li>Symptom check history</li>
                                    <li>Diagnosis records</li>
                                    <li>Subscription details</li>
                                </ul>
                                <span className="block mt-2">will be permanently deleted.</span>
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label>Type &quot;deactivate&quot; to confirm</Label>
                                <Input 
                                    value={deactivateConfirmText}
                                    onChange={(e) => setDeactivateConfirmText(e.target.value)}
                                    placeholder="deactivate"
                                    className="border-red-200 focus:border-red-500"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setShowDeactivateDialog(false)}>
                                Cancel
                            </Button>
                            <Button 
                                variant="destructive" 
                                onClick={handleDeactivateAccount}
                                disabled={deactivating || deactivateConfirmText.toLowerCase() !== 'deactivate'}
                            >
                                {deactivating ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Deactivating...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Deactivate Account
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
}
